import { Accordion, Flex, Link, LoadingIcon, Stack, Text } from "basis";
import { SvgInline } from "components/SvgInline/SvgInline";
import { useConfigFeature } from "library/hooks/useConfig";
import { useEventTracking, usePageTracking } from "react-event-tracker";
import { Layout as OldLayout } from "../layout";
import { Layout } from "../Layout/Layout";
import { RelayService } from "../RelayService/RelayService";
import { TimeoutModalHandler } from "../TimeoutModal/TimeoutModal";
import { Zendesk } from "../Zendesk/Zendesk";
import { General } from "./General";
import { LinkAccount } from "./LinkAccount";
import { Payments } from "./Payments";
import { Registration } from "./Registration";
import { Statements } from "./Statements";
import { Support } from "./Support";
import { FaqProvider, useFaqData } from "./useFaqData";

function Content() {
  const eStatementsActive = useConfigFeature("eStatements");
  const showLiveChatNZ = useConfigFeature("liveChatNZ");
  const showLiveChatAU = useConfigFeature("liveChatAU");
  const { trackEvent } = useEventTracking();
  const { isLoading, isError, data } = useFaqData();
  const hasAccounts =
    data?.hasCreditCards === true || data?.hasPersonalLoans === true;
  const items = [
    {
      id: "registration",
      component: Registration,
      showWhen: data?.isLoggedIn === false,
    },
    {
      id: "linkAccount",
      component: LinkAccount,
      showWhen: true,
    },
    {
      id: "payments",
      component: Payments,
      showWhen: data?.hasCreditCards === true,
    },
    {
      id: "statements",
      component: Statements,
      showWhen: eStatementsActive && hasAccounts,
    },
    {
      id: "general",
      component: General,
      showWhen: hasAccounts,
    },
    {
      id: "support",
      component: Support,
      showWhen: hasAccounts,
    },
  ];
  const hasOneAccordionItem =
    items.map((item) => item.showWhen).filter(Boolean).length === 1;

  return (
    <Layout>
      <Layout.Header>Frequently asked questions</Layout.Header>
      <Layout.Main>
        {(showLiveChatAU || showLiveChatNZ) && <Zendesk />}
        {isLoading ? (
          <Flex height="200" placeItems="center">
            <LoadingIcon size="medium" />
          </Flex>
        ) : isError ? (
          <Stack gap="8">
            <SvgInline name="error-dog" width="236" />
            <Text textStyle="heading5" textStyle-sm="heading4">
              We were unable to retrieve your accounts
            </Text>
            <Text>
              Please try again later or contact Latitude if the problem
              persists.
            </Text>
            <Link
              appearance="primary-button"
              width="216"
              href="/contact"
              newTab={false}
            >
              Contact Latitude
            </Link>
          </Stack>
        ) : (
          <>
            <Accordion
              onItemToggle={({ isOpen, itemHeaderChildren }) => {
                if (isOpen) {
                  trackEvent({
                    event: {
                      category: "accordion",
                      action: "show-content",
                      location: "Frequently asked questions",
                      label: itemHeaderChildren,
                    },
                  });
                }
              }}
            >
              {items.map(({ id, component: Component, showWhen }) =>
                showWhen ? (
                  <Component initiallyOpen={hasOneAccordionItem} key={id} />
                ) : null,
              )}
            </Accordion>
            {data.isLoggedIn && <TimeoutModalHandler />}
          </>
        )}
      </Layout.Main>
      <Layout.Sidebar>
        <RelayService />
      </Layout.Sidebar>
    </Layout>
  );
}

export const Faqs = ({ isLoggedIn }) => {
  usePageTracking({
    page: {
      pageType: "information-page",
      siteSection: "support",
      siteSubSection: "faqs",
      pageTitle: "homepage",
    },
    user: {
      authenticationStatus: isLoggedIn ? "logged in" : "not logged in",
    },
  });

  return (
    <OldLayout>
      <FaqProvider>
        <Content />
      </FaqProvider>
    </OldLayout>
  );
};
